import { isTextMimeType } from '@terra/shared-asset-processing';
import { normalizeHttpHeaders } from './http-pseudo-header.utils';
import { resolveEffectiveHttpVersion } from './http-version.utils';

export const BINARY_BODY_PREFIX = '[Binary:base64]';
export const HAR_BASE64_COMMENT = 'base64-encoded';

export type BodyEncoding = 'utf8' | 'base64';

export type HarBodyField = {
  text: string;
  encoding?: BodyEncoding;
  mimeType: string;
  comment?: string;
};

export type EncodedBodyForHar = {
  text: string;
  encoding: BodyEncoding;
  byteSize: number;
};

type HeaderEntry = { name: string; value: string };

export function parseBinaryBody(rawBody: string): {
  body: string;
  bodyEncoding: BodyEncoding | undefined;
} {
  if (rawBody.startsWith(BINARY_BODY_PREFIX)) {
    return {
      body: rawBody.slice(BINARY_BODY_PREFIX.length),
      bodyEncoding: 'base64',
    };
  }

  return { body: rawBody, bodyEncoding: undefined };
}

export function encodeBinaryBody(buffer: Buffer): string {
  return `${BINARY_BODY_PREFIX}${buffer.toString('base64')}`;
}

export function decodeBinaryBody(raw: string): Buffer {
  if (raw.startsWith(BINARY_BODY_PREFIX)) {
    return Buffer.from(raw.slice(BINARY_BODY_PREFIX.length), 'base64');
  }

  return Buffer.from(raw, 'utf-8');
}

export function estimateBase64ByteSize(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;

  return Math.floor((base64.length * 3) / 4) - padding;
}

export function computePortalBodyByteSize(body: string): number {
  if (!body) {
    return 0;
  }

  if (body.startsWith(BINARY_BODY_PREFIX)) {
    return estimateBase64ByteSize(body.slice(BINARY_BODY_PREFIX.length));
  }

  return Buffer.byteLength(body, 'utf8');
}

export function isMultipartContentType(contentType?: string): boolean {
  return (contentType ?? '').toLowerCase().includes('multipart/');
}

function latin1ToBase64(latin1: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(latin1, 'latin1').toString('base64');
  }

  const binary = Array.from({ length: latin1.length }, (_unused, index) =>
    String.fromCharCode(latin1.charCodeAt(index) & 0xff),
  ).join('');

  return btoa(binary);
}

function base64ToLatin1(base64: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('latin1');
  }

  return atob(base64);
}

export function encodeLatin1WireBodyToBase64(latin1: string): string {
  return latin1ToBase64(latin1);
}

export function isGrpcWebVisibleBase64Payload(text: string): boolean {
  const trimmed = text.replace(/\s/g, '');

  if (trimmed.length < 8) {
    return false;
  }

  if (!/^[A-Za-z0-9+/]+=*$/.test(trimmed)) {
    return false;
  }

  return trimmed.startsWith('AAAA');
}

export function decodeBinaryBodyForDisplay(body: string): string {
  if (!body.startsWith(BINARY_BODY_PREFIX)) return body;

  try {
    return base64ToLatin1(body.slice(BINARY_BODY_PREFIX.length));
  } catch {
    return body;
  }
}

export function encodeDisplayBodyForTransport(body: string): string {
  return `${BINARY_BODY_PREFIX}${latin1ToBase64(body)}`;
}

export function textIncludesPortalBinaryWireBody(text: string): boolean {
  return text.includes(BINARY_BODY_PREFIX);
}

type HeaderValue = string | string[] | undefined;
type HeadersMap = Record<string, HeaderValue> | undefined;

export function getHeaderValue(
  headers: HeadersMap,
  headerName: string,
): string | undefined {
  if (!headers) return undefined;
  const lowerHeaderName = headerName.toLowerCase();

  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() === lowerHeaderName) {
      return Array.isArray(value) ? value[0] : value;
    }
  }

  return undefined;
}

export function getContentTypeHeader(headers: HeadersMap): string {
  return getHeaderValue(headers, 'content-type') ?? '';
}

export function isHarBodyBase64({
  text,
  encoding,
  comment,
  mimeType,
}: {
  text?: string;
  encoding?: BodyEncoding;
  comment?: string;
  mimeType: string;
}): boolean {
  if (!text) {
    return false;
  }

  if (text.startsWith(BINARY_BODY_PREFIX)) {
    return true;
  }

  return (
    encoding === 'base64' ||
    comment === HAR_BASE64_COMMENT ||
    (!encoding && !comment && !isTextMimeType(mimeType))
  );
}

export function encodeBodyForHar({
  buffer,
  contentType,
}: {
  buffer: Buffer;
  contentType: string | string[] | undefined;
}): EncodedBodyForHar {
  const isBinary = !isTextMimeType(contentType);
  const encoding: BodyEncoding = isBinary ? 'base64' : 'utf8';

  return {
    text: buffer.toString(encoding),
    encoding,
    byteSize: buffer.length,
  };
}

export function resolveHarBodyToBuffer({
  text,
  encoding,
  comment,
  mimeType,
}: HarBodyField): Buffer {
  if (!text) {
    return Buffer.alloc(0);
  }

  if (text.startsWith(BINARY_BODY_PREFIX)) {
    return Buffer.from(text.slice(BINARY_BODY_PREFIX.length), 'base64');
  }

  if (isHarBodyBase64({ text, encoding, comment, mimeType })) {
    return Buffer.from(text, 'base64');
  }

  return Buffer.from(text, 'utf8');
}

export function resolveHarBodyForWireDisplay({
  text,
  encoding,
  comment,
  mimeType,
}: HarBodyField): string {
  if (!text) {
    return '';
  }

  if (text.startsWith(BINARY_BODY_PREFIX)) {
    return decodeBinaryBodyForDisplay(text);
  }

  if (!isHarBodyBase64({ text, encoding, comment, mimeType })) {
    return text;
  }

  const buffer = Buffer.from(text, 'base64');

  if (isTextMimeType(mimeType)) {
    return buffer.toString('utf8');
  }

  return buffer.toString('latin1');
}

export function computeHarBodyByteSize({
  text,
  encoding,
  comment,
  mimeType,
}: HarBodyField): number {
  if (!text) {
    return 0;
  }

  if (text.startsWith(BINARY_BODY_PREFIX)) {
    return estimateBase64ByteSize(text.slice(BINARY_BODY_PREFIX.length));
  }

  if (isHarBodyBase64({ text, encoding, comment, mimeType })) {
    return Buffer.from(text, 'base64').length;
  }

  return Buffer.byteLength(text, 'utf8');
}

export function portalBodyToHarField({
  body,
  mimeType,
  bodyEncoding,
}: {
  body: string;
  mimeType: string;
  bodyEncoding?: BodyEncoding;
}): {
  text: string;
  encoding?: BodyEncoding;
  comment?: string;
  mimeType: string;
} {
  const { body: parsedBody, bodyEncoding: parsedBodyEncoding } =
    parseBinaryBody(body);
  const effectiveEncoding = parsedBodyEncoding ?? bodyEncoding;
  const shouldMarkBase64 = isHarBodyBase64({
    text: parsedBody,
    mimeType,
    encoding: effectiveEncoding,
  });

  return {
    mimeType,
    text: parsedBody,
    ...(shouldMarkBase64
      ? { encoding: 'base64' as const, comment: HAR_BASE64_COMMENT }
      : {}),
  };
}

export function harWireDisplayToPortalBody({
  text,
  encoding,
  comment,
  mimeType,
}: HarBodyField): string {
  if (!text) {
    return '';
  }

  if (isHarBodyBase64({ text, encoding, comment, mimeType })) {
    const base64Text = text.startsWith(BINARY_BODY_PREFIX)
      ? text.slice(BINARY_BODY_PREFIX.length)
      : text;

    return `${BINARY_BODY_PREFIX}${base64Text}`;
  }

  return text;
}

export type HarPostDataParamLike = {
  name: string;
  value?: string;
};

export type HarPostDataLike = {
  mimeType: string;
  text?: string;
  comment?: string;
  params?: HarPostDataParamLike[];
};

export type HarResponseContentLike = {
  mimeType: string;
  text: string;
  encoding?: BodyEncoding;
};

export function decodeHarPostDataForApi(
  postData: HarPostDataLike | undefined,
): HarPostDataLike | undefined {
  if (!postData?.text) {
    return postData;
  }

  const isBase64 = isHarBodyBase64({
    text: postData.text,
    comment: postData.comment,
    mimeType: postData.mimeType,
  });

  if (!isBase64) {
    return postData;
  }

  if (!isTextMimeType(postData.mimeType)) {
    return {
      ...postData,
      comment: HAR_BASE64_COMMENT,
    };
  }

  try {
    return {
      ...postData,
      text: Buffer.from(postData.text, 'base64').toString('utf8'),
      comment: undefined,
    };
  } catch {
    return postData;
  }
}

export function decodeHarResponseContentForApi(
  content: HarResponseContentLike,
): HarResponseContentLike {
  if (!content.text) {
    return content;
  }

  const isBase64 = isHarBodyBase64({
    text: content.text,
    encoding: content.encoding,
    mimeType: content.mimeType,
  });

  if (!isBase64) {
    return content;
  }

  if (!isTextMimeType(content.mimeType)) {
    return {
      ...content,
      encoding: 'base64',
    };
  }

  try {
    return {
      ...content,
      text: Buffer.from(content.text, 'base64').toString('utf8'),
      encoding: undefined,
    };
  } catch {
    return content;
  }
}

export function decodeHarEntryBodiesForApi<
  TEntry extends {
    request: { postData?: HarPostDataLike };
    response: { content: HarResponseContentLike };
  },
>(harEntry: TEntry): TEntry {
  return {
    ...harEntry,
    request: {
      ...harEntry.request,
      postData: decodeHarPostDataForApi(harEntry.request.postData),
    },
    response: {
      ...harEntry.response,
      content: decodeHarResponseContentForApi(harEntry.response.content),
    },
  };
}

function getHarUrlPath(url: string): string {
  try {
    const urlObject = new URL(url);

    return urlObject.pathname + urlObject.search;
  } catch {
    return url;
  }
}

function formatHttpHeadersBlock(headers: HeaderEntry[]): string {
  return normalizeHttpHeaders(headers)
    .map((header) => `${header.name}: ${header.value}`)
    .join('\n');
}

function serializeHarPostDataParams(
  params: HarPostDataParamLike[] | undefined,
): string {
  if (!params?.length) {
    return '';
  }

  return params
    .map(
      (param) =>
        `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value ?? '')}`,
    )
    .join('&');
}

function resolveHarRequestBodyForHttpMessage(
  postData: HarPostDataLike | undefined,
): string {
  if (!postData) {
    return '';
  }

  if (postData.text) {
    return resolveHarBodyForWireDisplay({
      text: postData.text,
      comment: postData.comment,
      mimeType: postData.mimeType,
    });
  }

  return serializeHarPostDataParams(postData.params);
}

export function formatHarRequestAsHttpMessage({
  request,
}: {
  request: {
    method: string;
    url: string;
    httpVersion?: string;
    headers: HeaderEntry[];
    postData?: HarPostDataLike;
  };
}): string {
  const httpVersion = resolveEffectiveHttpVersion({
    httpVersion: request.httpVersion,
    headers: request.headers,
  });
  const requestLine = `${request.method} ${getHarUrlPath(request.url)} ${httpVersion}`;
  const headers = formatHttpHeadersBlock(request.headers);
  const body = resolveHarRequestBodyForHttpMessage(request.postData);

  return body
    ? [requestLine, headers, '', body].join('\n')
    : [requestLine, headers].join('\n');
}

export function formatHarResponseAsHttpMessage({
  response,
}: {
  response: {
    status: number;
    statusText?: string;
    httpVersion?: string;
    headers: HeaderEntry[];
    content: HarResponseContentLike;
  };
}): string {
  const httpVersion = resolveEffectiveHttpVersion({
    httpVersion: response.httpVersion,
    headers: response.headers,
  });
  const statusLine = `${httpVersion} ${response.status} ${response.statusText ?? ''}`;
  const headers = formatHttpHeadersBlock(response.headers);
  const body = response.content.text
    ? resolveHarBodyForWireDisplay({
        text: response.content.text,
        encoding: response.content.encoding,
        mimeType: response.content.mimeType,
      })
    : '';

  return body
    ? [statusLine, headers, '', body].join('\n')
    : [statusLine, headers].join('\n');
}
